import { Alert, Divider, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import AgricultureOutlinedIcon from "@mui/icons-material/AgricultureOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import { useEffect } from "react";
import CultivationsForm from "./FieldOperationsSubForms/CultivationsForm";
import PlantingForm from "./FieldOperationsSubForms/PlantingForm";
import FertilizerApplicationsForm from "./FieldOperationsSubForms/FertilizerApplicationsForm";
import FertilizerRatesForm from "./FieldOperationsSubForms/FertilizerRatesForm";
import PesticidesForm from "./FieldOperationsSubForms/PesticidesForm";
import FourRAssessment from "./FourRAssessment/FourRAssessment";

const DETAIL_OPTIONS = [
  {
    value: "standard",
    title: "Standard nutrient rates",
    description: "Enter nutrient totals by application stage in lb/acre.",
  },
  {
    value: "detailed",
    title: "Detailed fertilizer and 4R records",
    description: "Add soil tests, product mixes, application rates, and stewardship practices.",
  },
];

function activeRowCount(rows = []) {
  return rows.filter((row) =>
    Object.values(row || {}).some((value) =>
      typeof value === "number"
        ? value > 0
        : typeof value === "boolean"
        ? value
        : value !== "" && value !== null && value !== undefined
    )
  ).length;
}

function isNegative(value) {
  if (value === "" || value === null || value === undefined) return false;
  return !Number.isFinite(Number(value)) || Number(value) < 0;
}

function cropYearHasInvalidOperationNumbers(cropYear) {
  const operations = [
    cropYear.planting_operation,
    ...(cropYear.cultivations || []),
    ...(cropYear.fertilizer_applications || []),
    ...(cropYear.pesticide_applications || []),
  ].filter(Boolean);
  const operationInvalid = operations.some(
    (operation) =>
      isNegative(operation.hours_used) ||
      isNegative(operation.fuel_used_litres)
  );
  const rateInvalid = Object.values(cropYear.fertilizer_rates || {}).some(
    (rates) =>
      [rates?.pre_seed, rates?.with_seed, rates?.post_seed].some(isNegative)
  );
  return operationInvalid || rateInvalid;
}

function SectionLink({ href, icon, label, meta }) {
  return (
    <a
      className="flex min-w-[180px] flex-1 items-center gap-3 border-l-2 border-slate-200 px-3 py-2 text-slate-700 transition hover:border-[#F15D22] hover:bg-slate-50 hover:text-slate-950"
      href={href}
    >
      <span className="text-[#F15D22]">{icon}</span>
      <span>
        <span className="block text-sm font-bold">{label}</span>
        <span className="block text-xs text-slate-500">{meta}</span>
      </span>
    </a>
  );
}

export default function FieldOperationsForm({
  LowerPanel,
  panelControls,
  farm,
  setFarm,
  cropYear,
  setCropYear,
  fourRAssessment,
  setFourRAssessment,
  fourRState,
  setFourRState,
}) {
  const has4RAssessment = fourRState.has4RAssessment;
  const assessmentIsIncomplete = has4RAssessment && !fourRState.completed4R;
  const hasInvalidNumbers = cropYearHasInvalidOperationNumbers(cropYear);
  const cultivationCount = activeRowCount(cropYear.cultivations);
  const pesticideCount = activeRowCount(cropYear.pesticide_applications);

  useEffect(() => {
    const element = document.getElementById("scrollableDiv");
    if (element) element.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  function handleDetailMode(event) {
    const nextValue = event.target.value === "detailed";
    setFourRState((previous) => ({
      ...previous,
      has4RAssessment: nextValue,
      completed4R:
        nextValue && !previous.has4RAssessment
          ? false
          : previous.completed4R,
    }));
  }

  return (
    <div className="h-full w-full">
      <div className="mb-4">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#2f6f45]">
          Crop-year activity
        </p>
        <h3 className="mt-1 text-[24px] font-bold text-slate-900">Field Operations</h3>
        <p className="mt-1 max-w-[820px] text-sm leading-6 text-slate-600">
          Record each field pass once, then choose the level of fertilizer detail that matches your records.
        </p>
      </div>

      <nav aria-label="Field operation sections" className="mb-5 flex flex-wrap border-y border-slate-200 bg-[#fcfcfa] py-2 md:sticky md:top-0 md:z-20">
        <SectionLink
          href="#equipment-passes"
          icon={<AgricultureOutlinedIcon fontSize="small" />}
          label="Equipment passes"
          meta={`${cultivationCount} cultivation pass${cultivationCount === 1 ? "" : "es"}`}
        />
        <SectionLink
          href="#fertilizer-details"
          icon={<ScienceOutlinedIcon fontSize="small" />}
          label="Fertilizer details"
          meta={has4RAssessment ? "Detailed 4R records" : "Standard nutrient rates"}
        />
        <SectionLink
          href="#crop-protection"
          icon={<SpaOutlinedIcon fontSize="small" />}
          label="Crop protection"
          meta={`${pesticideCount} application${pesticideCount === 1 ? "" : "s"}`}
        />
      </nav>

      {hasInvalidNumbers ? (
        <Alert severity="error" className="mb-5 max-w-[820px]">
          Hours, fuel, and nutrient rates must be zero or greater before this
          crop year can be saved.
        </Alert>
      ) : null}

      <div id="equipment-passes">
        <p className="mb-4 max-w-[820px] text-sm text-slate-600">
          Equipment, hours, dates and recorded fuel are optional. Recorded litres
          and hours are not converted to Holos energy. Optional diesel energy
          (GJ/ha) can be set in Review; without an override, this input is zero.
        </p>
        <CultivationsForm cropYear={cropYear} setCropYear={setCropYear} farm={farm} setFarm={setFarm} />
        <PlantingForm cropYear={cropYear} setCropYear={setCropYear} farm={farm} setFarm={setFarm} />
      </div>

      <section id="fertilizer-details" className="mt-6 border-b border-slate-200 pb-6">
        <div className="mb-4">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Fertilizer</p>
          <h4 className="mt-1 text-[21px] font-bold text-slate-800">Applications and nutrient detail</h4>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Equipment and fuel describe the field pass. Nutrient details describe what was applied.
          </p>
        </div>

        <FertilizerApplicationsForm cropYear={cropYear} setCropYear={setCropYear} farm={farm} setFarm={setFarm} />

        <div className="mt-5 border-t border-slate-200 pt-5">
          <p className="text-sm font-bold text-slate-800">Choose your fertilizer detail level</p>
          <p className="mt-1 text-sm text-slate-600">Use detailed records when product-level information or 4R reporting is available.</p>
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <RadioGroup
              aria-label="Fertilizer detail level"
              name="fertilizer-detail-level"
              onChange={handleDetailMode}
              value={has4RAssessment ? "detailed" : "standard"}
            >
              <div className="grid gap-2 lg:grid-cols-2">
                {DETAIL_OPTIONS.map((option) => {
                  const selected = (has4RAssessment ? "detailed" : "standard") === option.value;
                  return (
                    <FormControlLabel
                      key={option.value}
                      className={`!m-0 min-h-[74px] border px-3 py-2 transition ${
                        selected ? "border-[#F15D22] bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      control={<Radio color="primary" />}
                      label={
                        <span>
                          <span className="block text-sm font-bold text-slate-800">{option.title}</span>
                          <span className="mt-0.5 block text-xs leading-5 text-slate-600">{option.description}</span>
                        </span>
                      }
                      value={option.value}
                    />
                  );
                })}
              </div>
            </RadioGroup>
          </FormControl>
        </div>

        {!has4RAssessment ? (
          <FertilizerRatesForm cropYear={cropYear} setCropYear={setCropYear} />
        ) : (
          <>
            <FourRAssessment
              cropYear={cropYear.crop_year}
              completed={fourRState.completed4R}
              fourRAssessment={fourRAssessment}
              setFourRAssessment={setFourRAssessment}
              setFourRState={setFourRState}
            />
            {assessmentIsIncomplete ? (
              <p className="mt-3 border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900" role="status">
                Soil tests and 4R practice records are optional for Holos. Save the fertilizer products and amounts you know; you can complete the other sections later.
              </p>
            ) : null}
          </>
        )}
      </section>

      <div id="crop-protection">
        <PesticidesForm cropYear={cropYear} setCropYear={setCropYear} farm={farm} setFarm={setFarm} />
      </div>

      <Divider />
      <LowerPanel
        activeStep={panelControls.activeStep}
        handleBack={panelControls.handleBack}
        handleNext={panelControls.handleNext}
        steps={panelControls.steps}
        handleComplete={panelControls.handleComplete}
        completedSteps={panelControls.completedSteps}
        totalSteps={panelControls.totalSteps}
        isInputValid={() => !hasInvalidNumbers}
      />
    </div>
  );
}
